import {
    ArrowRight,
    CheckCircle2,
    Clock,
    DollarSign,
    History,
    Minus,
    Target,
    TrendingDown,
    TrendingUp,
    XCircle,
} from 'lucide-react'
import { RenderArrow } from '../../lib/table-utils'
import { useEffect, useMemo, useState } from 'react'
import {
    calculateTradeIntelligence,
    type TradeIntelligenceInput,
    type TradeIntelligenceResult,
} from '../../lib/trade-intelligence'
import type { MarketSnapshot } from '../../types'

// --- Types ---

interface TradeEntry {
    id: string
    timestamp: number
    timeIST: string
    type: 'BULLISH' | 'BEARISH'
    subType: string
    action: string
    strike: string
    entryPrice: number
    stopLoss: number
    target: number
    exitPrice?: number
    exitReason?: 'SL HIT' | 'TARGET HIT' | 'REVERSAL' | 'MANUAL' | 'OPEN'
    pnlPoints: number
    status: 'OPEN' | 'CLOSED'
    score: number
}

interface SignalHistoryTableProps {
    currentSignal: TradeIntelligenceResult | null
    spotPrice: number
    symbol: string
    initialHistory?: MarketSnapshot[]
}

// --- Backtest Engine ---

const runBacktest = (history: MarketSnapshot[], symbol: string): TradeEntry[] => {
    const trades: TradeEntry[] = []
    let activeTrade: TradeEntry | null = null

    // DEBUG: Log history info
    // console.log(`[SignalHistoryTable] runBacktest called with ${history.length} snapshots for ${symbol}`)
    if (history.length > 0) {
        const first = history[0] as any
        // First snapshot structure check - silent
        /*
        console.log(`[SignalHistoryTable] First snapshot structure:`, {
            hasIndexData: !!first.indexData,
            hasSymbolData: !!first.indexData?.[symbol],
            hasData: !!first.data,
            dataSpotPrice: first.data?.spotPrice,
            dataIndex: first.data?.index,
            hasAnalysis: !!first.analysis,
            analysisSpotPrice: first.analysis?.spotPrice,
            topLevelKeys: Object.keys(first).slice(0, 10),
            timestamp: first.timestampIST || first.timestamp,
        })
        */
    }

    let skippedNoIndex = 0
    let skippedNoAnalysis = 0
    let signalsGenerated = 0

    // Iterate Chronologically
    for (let i = 0; i < history.length; i++) {
        const snap = history[i] as any

        // Try multiple locations for spot price
        let spot = 0
        if (snap.indexData?.[symbol]?.lastPrice) {
            spot = snap.indexData[symbol].lastPrice
        } else if (snap.data?.spotPrice) {
            spot = snap.data.spotPrice
        } else if (snap.analysis?.shift) {
            // Use shift as proxy for spot price (typically ATM)
            spot = snap.analysis.shift
        } else if (snap.data?.index) {
            spot = snap.data.index
        } else if (snap.analysis?.spotPrice) {
            spot = snap.analysis.spotPrice
        } else if (snap.spotPrice) {
            spot = snap.spotPrice
        }

        if (!spot) {
            skippedNoIndex++
            continue
        }

        const snapAnalysis = snap.analysis
        if (!snapAnalysis) {
            skippedNoAnalysis++
            continue
        }

        const timestamp = snap.timestamp || Date.now()

        // 1. Re-run Intelligence
        const input: TradeIntelligenceInput = {
            marketData: {
                symbol: symbol,
                spotPrice: spot,
                options: [],
                timestamp: timestamp,
                pcr: snap.pcr,
                vix: snap.vix,
            } as any,
            analysis: snapAnalysis,
            mode: 'OI' as any,
            history: history.slice(0, i), // Lookback
            rsiData: (snap as any).rsiData,
        }

        const result = calculateTradeIntelligence(input)
        if (!result || !result.setup) continue

        // 2. Manage Active Trade
        if (activeTrade) {
            let exitPrice = 0
            let exitReason: 'SL HIT' | 'TARGET HIT' | 'REVERSAL' | null = null

            if (activeTrade.type === 'BULLISH') {
                // SL: If spot drops below SL, use SPOT (Slippage/Gap awareness) matching real life worst case
                if (spot <= activeTrade.stopLoss) {
                    exitPrice = spot
                    exitReason = 'SL HIT'
                }
                // Target: If spot pops above Target, take Target (Limit Order fill assumption)
                else if (spot >= activeTrade.target) {
                    exitPrice = activeTrade.target
                    exitReason = 'TARGET HIT'
                }
                // Reversal: If signal flips to Bearish Strong/Moderate
                else if (result.signalType === 'BEARISH' && result.signalStrength !== 'WEAK') {
                    exitPrice = spot
                    exitReason = 'REVERSAL'
                }
            } // BEARISH
            else {
                // SL: If spot pops above SL
                if (spot >= activeTrade.stopLoss) {
                    exitPrice = spot
                    exitReason = 'SL HIT'
                }
                // Target: If spot drops below Target
                else if (spot <= activeTrade.target) {
                    exitPrice = activeTrade.target
                    exitReason = 'TARGET HIT'
                }
                // Reversal
                else if (result.signalType === 'BULLISH' && result.signalStrength !== 'WEAK') {
                    exitPrice = spot
                    exitReason = 'REVERSAL'
                }
            }

            if (exitReason) {
                // Close Trade
                activeTrade.exitPrice = exitPrice
                activeTrade.exitReason = exitReason
                activeTrade.status = 'CLOSED'
                // Calc P&L
                if (activeTrade.type === 'BULLISH') activeTrade.pnlPoints = exitPrice - activeTrade.entryPrice
                else activeTrade.pnlPoints = activeTrade.entryPrice - exitPrice

                trades.push(activeTrade)
                activeTrade = null // Reset
            }
        }

        // 3. Open New Trade (If no active trade)
        // Only open if Signal is BULLISH or BEARISH (not NEUTRAL)
        if (!activeTrade && (result.signalType === 'BULLISH' || result.signalType === 'BEARISH')) {
            signalsGenerated++

            // V5.3: Show all signals, no filtering by strength
            // Previously filtered weak and reversal_slow signals

            // Safe Parse Levels
            let sl = parseFloat(result.management.stopLoss) || 0
            let target = parseFloat(result.management.target) || 0

            // Fallback (Should be rare with fixed logic)
            if (sl === 0 || target === 0) {
                if (result.signalType === 'BULLISH') {
                    sl = spot - 20
                    target = spot + 40
                } else {
                    sl = spot + 20
                    target = spot - 40
                }
            }

            // No minimum profit filter - show all signals
            // const potentialProfit = Math.abs( target - spot );

            // Safe timestamp parsing with fallbacks
            let timeIST = new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
            if (snap.timestampIST) {
                // Handle both 'T' and space separators
                const timePart = snap.timestampIST.includes('T')
                    ? snap.timestampIST.split('T')[1]
                    : snap.timestampIST.split(' ')[1]
                if (timePart) timeIST = timePart.slice(0, 5)
            }

            activeTrade = {
                id: `trade-${i}`,
                timestamp: timestamp,
                timeIST,
                type: result.signalType,
                subType: result.signalSubType,
                action: result.setup.type,
                strike: result.setup.strike,
                entryPrice: spot,
                stopLoss: sl,
                target: target,
                pnlPoints: 0,
                status: 'OPEN',
                score: result.score,
            }
        }
    }

    // Add currently open trade if any
    if (activeTrade) {
        // Update live P&L for open trade
        const lastSnap = history[history.length - 1]
        const lastSpot = lastSnap?.indexData?.[symbol]?.lastPrice || activeTrade.entryPrice
        if (activeTrade.type === 'BULLISH') activeTrade.pnlPoints = lastSpot - activeTrade.entryPrice
        else activeTrade.pnlPoints = activeTrade.entryPrice - lastSpot

        trades.push(activeTrade)
    }

    // DEBUG: Summary log
    // Summary logging - silent
    /*
    console.log(`[SignalHistoryTable] Backtest complete:`, {
        totalSnapshots: history.length,
        skippedNoIndex,
        skippedNoAnalysis,
        signalsGenerated,
        tradesCreated: trades.length,
    })
    */

    return trades.reverse() // Newest first
}

export const SignalHistoryTable: React.FC<SignalHistoryTableProps> = ({
    currentSignal,
    spotPrice,
    symbol,
    initialHistory = [],
}) => {
    const [trades, setTrades] = useState<TradeEntry[]>([])

    // Run Backtest on History Update
    useEffect(() => {
        if (!initialHistory || initialHistory.length === 0) return

        // Defer calculation to avoid blocking UI
        const timeout = setTimeout(() => {
            const results = runBacktest(initialHistory, symbol)
            setTrades(results)
        }, 100)

        return () => clearTimeout(timeout)
    }, [initialHistory, symbol])

    // Stats
    const stats = useMemo(() => {
        const closed = trades.filter((t) => t.status === 'CLOSED')
        const wins = closed.filter((t) => t.pnlPoints > 0).length
        const losses = closed.filter((t) => t.pnlPoints <= 0).length
        const totalPnl = closed.reduce((acc, t) => acc + t.pnlPoints, 0)
        const openPnl = trades.filter((t) => t.status === 'OPEN').reduce((acc, t) => acc + t.pnlPoints, 0)

        return { wins, losses, totalPnl, openPnl, total: closed.length }
    }, [trades])

    if (trades.length === 0) {
        return (
            <div className="bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-slate-800 p-8 text-center">
                <History className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-slate-600 dark:text-slate-400">No Trades Triggered Yet</h3>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Waiting for high-probability setups...
                </p>
            </div>
        )
    }

    return (
        <div className="bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-slate-800 shadow-lg overflow-hidden">
            {/* Header / Stats */}
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 bg-white/60 dark:bg-slate-900/60">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                        <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                            Algo Backtest Journal
                        </h3>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                            Based on Intraday Data ({trades.length} trades)
                        </p>
                    </div>
                </div>

                {/* Scorecards */}
                <div className="flex items-center gap-3">
                    <div className="flex flex-col items-center px-4 py-2 bg-emerald-50/50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/50">
                        <span className="text-[10px] font-bold text-emerald-600/70 dark:text-emerald-400/70 uppercase">
                            Total Wins
                        </span>
                        <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">{stats.wins}</span>
                    </div>
                    <div className="flex flex-col items-center px-4 py-2 bg-rose-50/50 dark:bg-rose-900/20 rounded-xl border border-rose-100 dark:border-rose-800/50">
                        <span className="text-[10px] font-bold text-rose-600/70 dark:text-rose-400/70 uppercase">
                            Total Losses
                        </span>
                        <span className="text-lg font-black text-rose-600 dark:text-rose-400">{stats.losses}</span>
                    </div>
                    <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
                    <div
                        className={`flex flex-col items-end px-4 py-2 rounded-xl border ${stats.totalPnl >= 0 ? 'bg-emerald-100/50 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700' : 'bg-rose-100/50 border-rose-200 dark:bg-rose-900/30 dark:border-rose-700'}`}
                    >
                        <span className="text-[10px] font-bold text-slate-500 uppercase">Day P&L (Pts)</span>
                        <span
                            className={`text-xl font-black ${stats.totalPnl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}
                        >
                            {stats.totalPnl > 0 ? '+' : ''}
                            {stats.totalPnl.toFixed(0)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 uppercase text-[10px] font-bold tracking-wider border-b border-slate-200 dark:border-slate-700">
                            <th className="p-3 text-left">Time</th>
                            <th className="p-3 text-center">Signal</th>
                            <th className="p-3 text-left">Strike</th>
                            <th className="p-3 text-right">Entry</th>
                            <th className="p-3 text-right">SL</th>
                            <th className="p-3 text-right">Target</th>
                            <th className="p-3 text-right">Exit Price</th>
                            <th className="p-3 text-center">Status</th>
                            <th className="p-3 text-right">P&L</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {trades.map((trade) => (
                            <tr
                                key={trade.id}
                                className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${trade.status === 'OPEN' ? 'bg-indigo-50/40 dark:bg-indigo-900/10' : ''}`}
                            >
                                <td className="p-3">
                                    <div className="flex items-center gap-1.5 font-mono font-bold text-slate-700 dark:text-slate-300">
                                        <Clock size={12} className="text-slate-400" />
                                        {trade.timeIST}
                                    </div>
                                </td>
                                <td className="p-3 text-center">
                                    <SignalBadge type={trade.type} subType={trade.subType} />
                                </td>
                                <td className="p-3">
                                    <span className="font-mono font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                        {trade.strike}
                                    </span>
                                </td>
                                <td className="p-3 text-right font-mono text-slate-700 dark:text-slate-300">
                                    {trade.entryPrice.toFixed(0)}
                                </td>
                                <td className="p-3 text-right font-mono text-rose-600 dark:text-rose-400">
                                    {trade.stopLoss.toFixed(0)}
                                </td>
                                <td className="p-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                                    {trade.target.toFixed(0)}
                                </td>
                                <td className="p-3 text-right font-mono font-bold">
                                    {trade.exitPrice ? (
                                        trade.exitPrice.toFixed(0)
                                    ) : (
                                        <span className="text-slate-400">-</span>
                                    )}
                                </td>
                                <td className="p-3 text-center">
                                    {trade.status === 'OPEN' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[10px] font-bold animate-pulse">
                                            LIVE
                                        </span>
                                    ) : (
                                        <span
                                            className={`text-[10px] font-bold uppercase ${
                                                trade.exitReason === 'TARGET HIT'
                                                    ? 'text-emerald-500'
                                                    : trade.exitReason === 'SL HIT'
                                                      ? 'text-rose-500'
                                                      : 'text-amber-500'
                                            }`}
                                        >
                                            {trade.exitReason}
                                        </span>
                                    )}
                                </td>
                                <td className="p-3 text-right">
                                    <div
                                        className={`font-mono font-black inline-flex items-center gap-1 ${
                                            trade.pnlPoints > 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : trade.pnlPoints < 0
                                                  ? 'text-rose-600 dark:text-rose-400'
                                                  : 'text-slate-400'
                                        }`}
                                    >
                                        {trade.pnlPoints > 0 ? '+' : ''}
                                        {trade.pnlPoints.toFixed(0)}
                                        <RenderArrow value={trade.pnlPoints} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// Helper Component
const SignalBadge = ({ type, subType }: { type: string; subType: string }) => {
    const isBull = type === 'BULLISH'
    const colors = isBull
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
    const Icon = isBull ? TrendingUp : TrendingDown

    return (
        <div className="flex flex-col items-center">
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${colors}`}
            >
                <Icon size={10} />
                {type}
            </span>
            <span className="text-[8px] font-semibold text-slate-400 mt-0.5 capitalize">
                {subType.replace('_', ' ').toLowerCase()}
            </span>
        </div>
    )
}
